import { useEffect, useRef, useState } from 'react';

import { getCurrentFiscalYear } from 'components/partials/insight-modal/shared/halpers';
import type { InsightModalMetadata } from 'components/partials/insight-modal/shared/constants';

import { getClientGoals } from 'api/generated/client-goals/client-goals';

import { useCurrentUser } from 'state/current-user/current-user.thunk';

export interface GoalRecord {
  id: string | number;
  summary?: string;
  category?: string;
  type?: string;
  enteringClassYear?: string | number;
  [key: string]: unknown;
}

interface UseReportGoalsResult {
  loadingGoals: boolean;
  allGoals: GoalRecord[];
}

export const useReportGoals = (
  initialMetadata?: InsightModalMetadata,
): UseReportGoalsResult => {
  const currentUser = useCurrentUser();
  const userId = currentUser?.data?.oktaUserId;
  const [loadingGoals, setLoadingGoals] = useState(false);
  const [allGoals, setAllGoals] = useState<GoalRecord[]>([]);
  const goalsLoadedRef = useRef(false);

  useEffect(() => {
    const fetchGoals = async () => {
      if (goalsLoadedRef.current) return;

      try {
        setLoadingGoals(true);
        const metaClientId = initialMetadata?.clientId;
        const clientId = metaClientId || currentUser?.data?.client?.id;
        const year = getCurrentFiscalYear();

        if (!userId || !clientId) return;

        const res = await getClientGoals(userId, Number(clientId), {
          'enteringClassYear.equals': Number(year),
          size: 1000,
          sort: ['createdDate,desc'],
        });
        const data: GoalRecord[] = (res.content ?? []).flatMap((g) =>
          g.id != null ? [{ ...g, id: g.id }] : [],
        );

        setAllGoals(data);
        goalsLoadedRef.current = true;
      } catch {
        setAllGoals([]);
      } finally {
        setLoadingGoals(false);
      }
    };

    fetchGoals();
  }, [currentUser?.data, userId, initialMetadata?.clientId]);

  return { loadingGoals, allGoals };
};
